import React from 'react';

/**
 * Bespoke Ancient-Futuristic Icons for ElchmE
 * 
 * These icons blend sacred geometry, alchemical symbols, and futuristic elements
 * to create a premium, unique visual language for the ElchmE experience.
 */

// Observe Icon - The All-Seeing Eye with Sacred Geometry
export const ObserveIcon = ({ className = "", size = 24, color = "#3B82F6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.3))' }}
  >
    {/* Outer sacred circle */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" strokeDasharray="2 4" />
    
    {/* Eye shape - almond/vesica piscis */}
    <path 
      d="M4 24C4 24 12 10 24 10C36 10 44 24 44 24C44 24 36 38 24 38C12 38 4 24 4 24Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeOpacity="0.8"
      fill={color}
      fillOpacity="0.05"
    />
    
    {/* Inner iris - geometric */}
    <circle cx="24" cy="24" r="8" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.15" />
    
    {/* Pupil - the void that sees */}
    <circle cx="24" cy="24" r="4" fill={color} fillOpacity="0.6" />
    
    {/* Light reflection - consciousness spark */}
    <circle cx="22" cy="22" r="1.5" fill="white" fillOpacity="0.8" />
    
    {/* Radiating awareness lines */}
    <g stroke={color} strokeWidth="0.5" strokeOpacity="0.4">
      <line x1="24" y1="2" x2="24" y2="8" />
      <line x1="24" y1="40" x2="24" y2="46" />
      <line x1="2" y1="24" x2="8" y2="24" />
      <line x1="40" y1="24" x2="46" y2="24" />
    </g>
    
    {/* Corner triangles - ancient wisdom markers */}
    <path d="M6 6L10 10M42 6L38 10M6 42L10 38M42 42L38 38" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" />
  </svg>
);

// Feel Icon - The Sacred Heart with Emanating Waves
export const FeelIcon = ({ className = "", size = 24, color = "#EF4444" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(239, 68, 68, 0.3))' }}
  >
    {/* Emanating pulse rings */}
    <circle cx="24" cy="26" r="20" stroke={color} strokeWidth="0.3" strokeOpacity="0.15" fill="none" />
    <circle cx="24" cy="26" r="17" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <circle cx="24" cy="26" r="14" stroke={color} strokeWidth="0.4" strokeOpacity="0.25" fill="none" />
    
    {/* Heart shape - sacred geometry proportions */}
    <path 
      d="M24 42L6 24C6 24 2 18 6 12C10 6 18 8 24 16C30 8 38 6 42 12C46 18 42 24 42 24L24 42Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeOpacity="0.9"
      fill={color}
      fillOpacity="0.1"
      strokeLinejoin="round"
    />
    
    {/* Inner heart glow */}
    <path 
      d="M24 36L12 24C12 24 10 20 12 16C14 12 20 13 24 19C28 13 34 12 36 16C38 20 36 24 36 24L24 36Z" 
      fill={color}
      fillOpacity="0.2"
    />
    
    {/* Heart center - the fire within */}
    <circle cx="24" cy="24" r="3" fill={color} fillOpacity="0.5" />
    
    {/* Upward flame/spirit */}
    <path 
      d="M24 8L22 12L24 10L26 12L24 8Z" 
      fill={color} 
      fillOpacity="0.4"
    />
  </svg>
);

// Need Icon - The Seed of Life / Growth Spiral
export const NeedIcon = ({ className = "", size = 24, color = "#10B981" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.3))' }}
  >
    {/* Outer boundary - the field of possibility */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    
    {/* Seed of life pattern - sacred geometry */}
    <circle cx="24" cy="24" r="8" stroke={color} strokeWidth="0.8" strokeOpacity="0.4" fill={color} fillOpacity="0.05" />
    <circle cx="24" cy="16" r="8" stroke={color} strokeWidth="0.5" strokeOpacity="0.25" fill="none" />
    <circle cx="24" cy="32" r="8" stroke={color} strokeWidth="0.5" strokeOpacity="0.25" fill="none" />
    <circle cx="17" cy="20" r="8" stroke={color} strokeWidth="0.5" strokeOpacity="0.25" fill="none" />
    <circle cx="31" cy="20" r="8" stroke={color} strokeWidth="0.5" strokeOpacity="0.25" fill="none" />
    <circle cx="17" cy="28" r="8" stroke={color} strokeWidth="0.5" strokeOpacity="0.25" fill="none" />
    <circle cx="31" cy="28" r="8" stroke={color} strokeWidth="0.5" strokeOpacity="0.25" fill="none" />
    
    {/* Central seed/sprout */}
    <ellipse cx="24" cy="28" rx="4" ry="6" fill={color} fillOpacity="0.3" />
    
    {/* Sprouting leaves */}
    <path 
      d="M24 22C24 22 20 16 24 10C28 16 24 22 24 22Z" 
      fill={color} 
      fillOpacity="0.5"
      stroke={color}
      strokeWidth="0.5"
    />
    <path 
      d="M24 20C24 20 18 18 14 22C20 22 24 20 24 20Z" 
      fill={color} 
      fillOpacity="0.3"
    />
    <path 
      d="M24 20C24 20 30 18 34 22C28 22 24 20 24 20Z" 
      fill={color} 
      fillOpacity="0.3"
    />
    
    {/* Root system - grounding */}
    <path 
      d="M24 34L22 40M24 34L24 42M24 34L26 40" 
      stroke={color} 
      strokeWidth="0.8" 
      strokeOpacity="0.4"
      strokeLinecap="round"
    />
  </svg>
);

// Gold Icon - The Philosopher's Stone / Transmutation Star
export const GoldIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 6px rgba(245, 158, 11, 0.4))' }}
  >
    {/* Outer alchemical circle */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" strokeDasharray="1 3" />
    
    {/* Eight-pointed star - the star of regeneration */}
    <path 
      d="M24 2L27 18L44 14L30 24L44 34L27 30L24 46L21 30L4 34L18 24L4 14L21 18L24 2Z" 
      stroke={color} 
      strokeWidth="1" 
      strokeOpacity="0.6"
      fill={color}
      fillOpacity="0.1"
      strokeLinejoin="round"
    />
    
    {/* Inner diamond - the philosopher's stone */}
    <path 
      d="M24 12L32 24L24 36L16 24L24 12Z" 
      stroke={color} 
      strokeWidth="1.2" 
      strokeOpacity="0.8"
      fill={color}
      fillOpacity="0.2"
    />
    
    {/* Center sun - pure gold */}
    <circle cx="24" cy="24" r="6" fill={color} fillOpacity="0.4" />
    <circle cx="24" cy="24" r="3" fill={color} fillOpacity="0.7" />
    
    {/* Light burst */}
    <circle cx="23" cy="23" r="1" fill="white" fillOpacity="0.9" />
    
    {/* Radiating rays */}
    <g stroke={color} strokeWidth="0.5" strokeOpacity="0.3">
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
        <line 
          key={i}
          x1={24 + Math.cos(angle * Math.PI / 180) * 8}
          y1={24 + Math.sin(angle * Math.PI / 180) * 8}
          x2={24 + Math.cos(angle * Math.PI / 180) * 11}
          y2={24 + Math.sin(angle * Math.PI / 180) * 11}
        />
      ))}
    </g>
  </svg>
);

// Request Icon - The Bridge / Connection Symbol
export const RequestIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.3))' }}
  >
    {/* Connection field */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    
    {/* Two beings */}
    <circle cx="12" cy="24" r="6" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.15" />
    <circle cx="36" cy="24" r="6" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.15" />
    
    {/* Bridge/connection */}
    <path 
      d="M18 24C18 24 24 16 30 24" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeOpacity="0.6"
      fill="none"
      strokeLinecap="round"
    />
    <path 
      d="M18 24C18 24 24 32 30 24" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeOpacity="0.6"
      fill="none"
      strokeLinecap="round"
    />
    
    {/* Center meeting point */}
    <circle cx="24" cy="24" r="2" fill={color} fillOpacity="0.5" />
    
    {/* Energy flow arrows */}
    <path d="M20 20L24 24L20 28" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" fill="none" />
    <path d="M28 20L24 24L28 28" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" fill="none" />
  </svg>
);

// Compass Icon - Navigation / Journey
export const CompassIcon = ({ className = "", size = 24, color = "#06B6D4" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(6, 182, 212, 0.3))' }}
  >
    {/* Outer ring with cardinal markers */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="18" stroke={color} strokeWidth="1" strokeOpacity="0.4" fill={color} fillOpacity="0.03" />
    
    {/* Cardinal points */}
    <g fill={color} fillOpacity="0.6">
      <polygon points="24,4 22,10 26,10" /> {/* N */}
      <polygon points="44,24 38,22 38,26" /> {/* E */}
      <polygon points="24,44 26,38 22,38" /> {/* S */}
      <polygon points="4,24 10,26 10,22" /> {/* W */}
    </g>
    
    {/* Compass rose */}
    <path 
      d="M24 8L28 24L24 40L20 24L24 8Z" 
      fill={color} 
      fillOpacity="0.2"
      stroke={color}
      strokeWidth="0.5"
    />
    <path 
      d="M8 24L24 20L40 24L24 28L8 24Z" 
      fill={color} 
      fillOpacity="0.15"
      stroke={color}
      strokeWidth="0.5"
    />
    
    {/* Center pivot */}
    <circle cx="24" cy="24" r="3" fill={color} fillOpacity="0.5" />
    <circle cx="24" cy="24" r="1.5" fill="white" fillOpacity="0.8" />
  </svg>
);

// Wisdom Icon - The Book of Light
export const WisdomIcon = ({ className = "", size = 24, color = "#F472B6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(244, 114, 182, 0.3))' }}
  >
    {/* Aura */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.15" fill="none" />
    
    {/* Open book pages */}
    <path 
      d="M8 12C8 12 16 14 24 10C32 14 40 12 40 12L40 38C40 38 32 36 24 40C16 36 8 38 8 38L8 12Z" 
      stroke={color} 
      strokeWidth="1" 
      fill={color}
      fillOpacity="0.1"
    />
    
    {/* Center spine */}
    <line x1="24" y1="10" x2="24" y2="40" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
    
    {/* Light emanating from pages */}
    <path 
      d="M24 6L22 10L24 8L26 10L24 6Z" 
      fill={color} 
      fillOpacity="0.6"
    />
    
    {/* Page lines - left */}
    <g stroke={color} strokeWidth="0.3" strokeOpacity="0.3">
      <line x1="12" y1="18" x2="20" y2="16" />
      <line x1="12" y1="24" x2="20" y2="22" />
      <line x1="12" y1="30" x2="20" y2="28" />
    </g>
    
    {/* Page lines - right */}
    <g stroke={color} strokeWidth="0.3" strokeOpacity="0.3">
      <line x1="28" y1="16" x2="36" y2="18" />
      <line x1="28" y1="22" x2="36" y2="24" />
      <line x1="28" y1="28" x2="36" y2="30" />
    </g>
    
    {/* Eye of wisdom in center */}
    <ellipse cx="24" cy="25" rx="4" ry="2" stroke={color} strokeWidth="0.5" fill={color} fillOpacity="0.2" />
    <circle cx="24" cy="25" r="1" fill={color} fillOpacity="0.6" />
  </svg>
);

// Sparkle Icon - Magic/Transformation
export const SparkleIcon = ({ className = "", size = 24, color = "#FBBF24" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 6px rgba(251, 191, 36, 0.5))' }}
  >
    {/* Main four-pointed star */}
    <path 
      d="M24 4L28 20L44 24L28 28L24 44L20 28L4 24L20 20L24 4Z" 
      fill={color} 
      fillOpacity="0.3"
      stroke={color}
      strokeWidth="1"
      strokeOpacity="0.8"
    />
    
    {/* Inner glow */}
    <path 
      d="M24 12L26 22L36 24L26 26L24 36L22 26L12 24L22 22L24 12Z" 
      fill={color} 
      fillOpacity="0.5"
    />
    
    {/* Center bright point */}
    <circle cx="24" cy="24" r="3" fill={color} fillOpacity="0.8" />
    <circle cx="24" cy="24" r="1.5" fill="white" fillOpacity="0.9" />
    
    {/* Smaller sparkles */}
    <g fill={color} fillOpacity="0.4">
      <path d="M10 10L11 13L14 14L11 15L10 18L9 15L6 14L9 13L10 10Z" />
      <path d="M38 10L39 13L42 14L39 15L38 18L37 15L34 14L37 13L38 10Z" />
      <path d="M10 38L11 35L14 34L11 33L10 30L9 33L6 34L9 35L10 38Z" />
      <path d="M38 38L39 35L42 34L39 33L38 30L37 33L34 34L37 35L38 38Z" />
    </g>
  </svg>
);

// Home Icon - The Sacred Hearth
export const HomeIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.3))' }}
  >
    {/* Protective circle */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    
    {/* House structure - sacred geometry proportions */}
    <path 
      d="M24 6L42 22V42H6V22L24 6Z" 
      stroke={color} 
      strokeWidth="1" 
      strokeOpacity="0.6"
      fill={color}
      fillOpacity="0.08"
      strokeLinejoin="round"
    />
    
    {/* Roof peak with radiance */}
    <path 
      d="M24 6L6 22H42L24 6Z" 
      stroke={color} 
      strokeWidth="1.2" 
      strokeOpacity="0.8"
      fill={color}
      fillOpacity="0.15"
      strokeLinejoin="round"
    />
    
    {/* Door - the threshold */}
    <path 
      d="M18 42V28C18 26 20 24 24 24C28 24 30 26 30 28V42" 
      stroke={color} 
      strokeWidth="1" 
      fill={color}
      fillOpacity="0.2"
    />
    
    {/* Inner hearth flame */}
    <path 
      d="M24 38C24 38 21 34 24 30C27 34 24 38 24 38Z" 
      fill={color} 
      fillOpacity="0.5"
    />
    
    {/* Welcome light from windows */}
    <circle cx="14" cy="30" r="2" fill={color} fillOpacity="0.3" />
    <circle cx="34" cy="30" r="2" fill={color} fillOpacity="0.3" />
  </svg>
);

// Profile Icon - The Self/Soul
export const ProfileIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.3))' }}
  >
    {/* Aura rings */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.15" fill="none" />
    <circle cx="24" cy="24" r="19" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" strokeDasharray="2 3" />
    
    {/* Head - consciousness center */}
    <circle cx="24" cy="16" r="8" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.15" />
    
    {/* Third eye */}
    <circle cx="24" cy="14" r="1.5" fill={color} fillOpacity="0.4" />
    
    {/* Body - vessel */}
    <path 
      d="M10 44C10 34 16 28 24 28C32 28 38 34 38 44" 
      stroke={color} 
      strokeWidth="1" 
      fill={color}
      fillOpacity="0.1"
    />
    
    {/* Heart center */}
    <circle cx="24" cy="36" r="3" fill={color} fillOpacity="0.3" />
    
    {/* Crown energy */}
    <g stroke={color} strokeWidth="0.5" strokeOpacity="0.4">
      <line x1="24" y1="4" x2="24" y2="7" />
      <line x1="20" y1="5" x2="21" y2="8" />
      <line x1="28" y1="5" x2="27" y2="8" />
    </g>
  </svg>
);

// Shield Icon - Protection/Safety
export const ShieldIcon = ({ className = "", size = 24, color = "#10B981" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.3))' }}
  >
    {/* Outer protective field */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.15" fill="none" />
    
    {/* Shield shape */}
    <path 
      d="M24 4C24 4 8 8 8 18C8 30 24 44 24 44C24 44 40 30 40 18C40 8 24 4 24 4Z" 
      stroke={color} 
      strokeWidth="1.2" 
      strokeOpacity="0.8"
      fill={color}
      fillOpacity="0.1"
    />
    
    {/* Inner shield layer */}
    <path 
      d="M24 10C24 10 14 13 14 20C14 28 24 38 24 38C24 38 34 28 34 20C34 13 24 10 24 10Z" 
      stroke={color} 
      strokeWidth="0.5" 
      strokeOpacity="0.5"
      fill={color}
      fillOpacity="0.15"
    />
    
    {/* Central symbol - flower of protection */}
    <circle cx="24" cy="22" r="6" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.2" />
    <circle cx="24" cy="22" r="3" fill={color} fillOpacity="0.4" />
    
    {/* Radiating strength */}
    <g stroke={color} strokeWidth="0.4" strokeOpacity="0.3">
      <line x1="24" y1="14" x2="24" y2="16" />
      <line x1="30" y1="22" x2="32" y2="22" />
      <line x1="16" y1="22" x2="18" y2="22" />
      <line x1="24" y1="28" x2="24" y2="30" />
    </g>
  </svg>
);

// Clock Icon - Sacred Time
export const ClockIcon = ({ className = "", size = 24, color = "#06B6D4" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(6, 182, 212, 0.3))' }}
  >
    {/* Outer eternity ring */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    
    {/* Clock face */}
    <circle cx="24" cy="24" r="18" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.08" />
    
    {/* Hour markers - 12 points like zodiac */}
    <g fill={color} fillOpacity="0.5">
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
        <circle 
          key={i}
          cx={24 + Math.sin(angle * Math.PI / 180) * 15}
          cy={24 - Math.cos(angle * Math.PI / 180) * 15}
          r={i % 3 === 0 ? 1.5 : 0.8}
        />
      ))}
    </g>
    
    {/* Hour hand */}
    <line x1="24" y1="24" x2="24" y2="14" stroke={color} strokeWidth="2" strokeOpacity="0.7" strokeLinecap="round" />
    
    {/* Minute hand */}
    <line x1="24" y1="24" x2="32" y2="18" stroke={color} strokeWidth="1.2" strokeOpacity="0.6" strokeLinecap="round" />
    
    {/* Center - the eternal now */}
    <circle cx="24" cy="24" r="3" fill={color} fillOpacity="0.4" />
    <circle cx="24" cy="24" r="1.5" fill="white" fillOpacity="0.8" />
  </svg>
);

// Users/Community Icon - The Circle
export const UsersIcon = ({ className = "", size = 24, color = "#EC4899" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(236, 72, 153, 0.3))' }}
  >
    {/* The great circle of belonging */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="18" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill={color} fillOpacity="0.03" strokeDasharray="3 2" />
    
    {/* Center figure */}
    <circle cx="24" cy="18" r="5" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.2" />
    <path d="M16 38C16 30 20 26 24 26C28 26 32 30 32 38" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.15" />
    
    {/* Left figure */}
    <circle cx="12" cy="22" r="4" stroke={color} strokeWidth="0.6" fill={color} fillOpacity="0.15" />
    <path d="M6 40C6 34 8 30 12 30C16 30 18 34 18 40" stroke={color} strokeWidth="0.6" fill={color} fillOpacity="0.1" />
    
    {/* Right figure */}
    <circle cx="36" cy="22" r="4" stroke={color} strokeWidth="0.6" fill={color} fillOpacity="0.15" />
    <path d="M30 40C30 34 32 30 36 30C40 30 42 34 42 40" stroke={color} strokeWidth="0.6" fill={color} fillOpacity="0.1" />
    
    {/* Connection lines - the bonds */}
    <path d="M16 20C20 22 28 22 32 20" stroke={color} strokeWidth="0.4" strokeOpacity="0.4" fill="none" strokeDasharray="2 2" />
  </svg>
);

// Bridge Icon - Connection between hearts
export const BridgeIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 5px rgba(139, 92, 246, 0.4))' }}
  >
    {/* Background glow */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.4" strokeOpacity="0.2" fill="none" />
    
    {/* Left heart/person */}
    <circle cx="10" cy="22" r="6" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <path d="M8 25 Q6 28, 8 32 Q10 36, 12 32 Q14 28, 12 25" fill={color} fillOpacity="0.3" stroke={color} strokeWidth="0.5" />
    
    {/* Right heart/person */}
    <circle cx="38" cy="22" r="6" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <path d="M36 25 Q34 28, 36 32 Q38 36, 40 32 Q42 28, 40 25" fill={color} fillOpacity="0.3" stroke={color} strokeWidth="0.5" />
    
    {/* The Bridge - an arc connecting both */}
    <path 
      d="M16 24 Q24 12 32 24" 
      stroke={color} 
      strokeWidth="2" 
      fill="none" 
      strokeLinecap="round"
    />
    
    {/* Bridge pillars */}
    <line x1="16" y1="24" x2="16" y2="34" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <line x1="32" y1="24" x2="32" y2="34" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    
    {/* Foundation */}
    <line x1="12" y1="34" x2="36" y2="34" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
    
    {/* Energy/understanding flowing across */}
    <circle cx="20" cy="18" r="1.5" fill={color} fillOpacity="0.6" />
    <circle cx="24" cy="15" r="2" fill={color} fillOpacity="0.8" />
    <circle cx="28" cy="18" r="1.5" fill={color} fillOpacity="0.6" />
  </svg>
);

// Play/Begin Icon - The Portal
export const PlayIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 5px rgba(245, 158, 11, 0.4))' }}
  >
    {/* Outer portal ring */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="19" stroke={color} strokeWidth="1" strokeOpacity="0.4" fill={color} fillOpacity="0.05" />
    
    {/* Play triangle - the arrow of intention */}
    <path 
      d="M18 12L38 24L18 36V12Z" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeOpacity="0.8"
      fill={color}
      fillOpacity="0.3"
      strokeLinejoin="round"
    />
    
    {/* Inner glow */}
    <path 
      d="M22 18L32 24L22 30V18Z" 
      fill={color}
      fillOpacity="0.5"
    />
    
    {/* Energy point */}
    <circle cx="26" cy="24" r="2" fill={color} fillOpacity="0.7" />
  </svg>
);

// Flow Icon - The River/Journey
export const FlowIcon = ({ className = "", size = 24, color = "#06B6D4" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(6, 182, 212, 0.3))' }}
  >
    {/* Outer boundary */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    
    {/* Flowing wave paths */}
    <path 
      d="M4 24C8 18 14 18 18 24C22 30 28 30 32 24C36 18 42 18 46 24" 
      stroke={color} 
      strokeWidth="2" 
      strokeOpacity="0.6"
      fill="none"
      strokeLinecap="round"
    />
    <path 
      d="M4 32C8 26 14 26 18 32C22 38 28 38 32 32C36 26 42 26 46 32" 
      stroke={color} 
      strokeWidth="1" 
      strokeOpacity="0.3"
      fill="none"
      strokeLinecap="round"
    />
    <path 
      d="M4 16C8 10 14 10 18 16C22 22 28 22 32 16C36 10 42 10 46 16" 
      stroke={color} 
      strokeWidth="1" 
      strokeOpacity="0.3"
      fill="none"
      strokeLinecap="round"
    />
    
    {/* Flow particles */}
    <circle cx="12" cy="21" r="1.5" fill={color} fillOpacity="0.5" />
    <circle cx="24" cy="27" r="1.5" fill={color} fillOpacity="0.5" />
    <circle cx="36" cy="21" r="1.5" fill={color} fillOpacity="0.5" />
  </svg>
);

// Stack Icon - Board/Kanban view
export const StackIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 2px rgba(139, 92, 246, 0.3))' }}
  >
    {/* Three stacked horizontal bars representing columns */}
    <rect x="3" y="4" width="18" height="4" rx="1" fill={color} fillOpacity="0.7" />
    <rect x="3" y="10" width="18" height="4" rx="1" fill={color} fillOpacity="0.5" />
    <rect x="3" y="16" width="18" height="4" rx="1" fill={color} fillOpacity="0.3" />
  </svg>
);

// Arrow Icon - Direction/Progress
export const ArrowIcon = ({ className = "", size = 24, color = "#9CA3AF", direction = "right" }) => {
  const rotations = { right: 0, down: 90, left: 180, up: 270 };
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 48 48" 
      fill="none" 
      className={className}
      style={{ 
        filter: 'drop-shadow(0 0 2px rgba(156, 163, 175, 0.2))',
        transform: `rotate(${rotations[direction] || 0}deg)`
      }}
    >
      <path 
        d="M18 8L34 24L18 40" 
        stroke={color} 
        strokeWidth="2" 
        strokeOpacity="0.6"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path 
        d="M22 16L30 24L22 32" 
        stroke={color} 
        strokeWidth="1" 
        strokeOpacity="0.3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Info Icon - The Light of Knowledge
export const InfoIcon = ({ className = "", size = 24, color = "#3B82F6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.3))' }}
  >
    {/* Outer wisdom circle */}
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="1" strokeOpacity="0.5" fill={color} fillOpacity="0.08" />
    
    {/* The "i" - pillar of knowledge */}
    <circle cx="24" cy="14" r="3" fill={color} fillOpacity="0.6" />
    <rect x="21" y="20" width="6" height="16" rx="2" fill={color} fillOpacity="0.5" />
    
    {/* Radiating insight */}
    <g stroke={color} strokeWidth="0.5" strokeOpacity="0.3">
      <line x1="24" y1="2" x2="24" y2="6" />
      <line x1="40" y1="8" x2="37" y2="11" />
      <line x1="46" y1="24" x2="42" y2="24" />
      <line x1="40" y1="40" x2="37" y2="37" />
      <line x1="8" y1="40" x2="11" y2="37" />
      <line x1="2" y1="24" x2="6" y2="24" />
      <line x1="8" y1="8" x2="11" y2="11" />
    </g>
  </svg>
);

// Refresh/Transform Icon - The Ouroboros
export const RefreshIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.3))' }}
  >
    {/* Outer cycle */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    
    {/* The eternal cycle - two interlocking curves */}
    <path 
      d="M24 6C35 6 42 15 42 24C42 28 40 32 36 35" 
      stroke={color} 
      strokeWidth="2" 
      strokeOpacity="0.6"
      fill="none"
      strokeLinecap="round"
    />
    <path 
      d="M24 42C13 42 6 33 6 24C6 20 8 16 12 13" 
      stroke={color} 
      strokeWidth="2" 
      strokeOpacity="0.6"
      fill="none"
      strokeLinecap="round"
    />
    
    {/* Arrow heads - direction of transformation */}
    <path d="M36 35L38 30L33 32L36 35Z" fill={color} fillOpacity="0.6" />
    <path d="M12 13L10 18L15 16L12 13Z" fill={color} fillOpacity="0.6" />
    
    {/* Center - the still point */}
    <circle cx="24" cy="24" r="4" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <circle cx="24" cy="24" r="2" fill={color} fillOpacity="0.5" />
  </svg>
);

// Message/Communication Icon - The Sacred Speech
export const MessageIcon = ({ className = "", size = 24, color = "#10B981" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.3))' }}
  >
    {/* Communication field */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.15" fill="none" />
    
    {/* Speech bubble - sacred vessel of words */}
    <path 
      d="M8 12C8 10 10 8 12 8H36C38 8 40 10 40 12V28C40 30 38 32 36 32H20L12 40V32H12C10 32 8 30 8 28V12Z" 
      stroke={color} 
      strokeWidth="1" 
      fill={color}
      fillOpacity="0.1"
    />
    
    {/* Words becoming light - three ascending dots */}
    <circle cx="18" cy="20" r="2" fill={color} fillOpacity="0.4" />
    <circle cx="24" cy="20" r="2" fill={color} fillOpacity="0.5" />
    <circle cx="30" cy="20" r="2" fill={color} fillOpacity="0.6" />
    
    {/* Rising intention */}
    <path d="M24 14L22 18H26L24 14Z" fill={color} fillOpacity="0.3" />
  </svg>
);

// Star/Favorite Icon - The Guiding Light
export const StarIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 6px rgba(245, 158, 11, 0.4))' }}
  >
    {/* Celestial field */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    
    {/* Five-pointed star - the human form reaching upward */}
    <path 
      d="M24 4L28 18L44 18L31 28L36 44L24 34L12 44L17 28L4 18L20 18L24 4Z" 
      stroke={color} 
      strokeWidth="1" 
      strokeOpacity="0.7"
      fill={color}
      fillOpacity="0.2"
      strokeLinejoin="round"
    />
    
    {/* Inner glow */}
    <path 
      d="M24 12L26 20L34 20L28 26L30 34L24 30L18 34L20 26L14 20L22 20L24 12Z" 
      fill={color}
      fillOpacity="0.4"
    />
    
    {/* Center radiance */}
    <circle cx="24" cy="24" r="4" fill={color} fillOpacity="0.6" />
    <circle cx="24" cy="24" r="2" fill="white" fillOpacity="0.8" />
  </svg>
);

// Love & Compassion Icon - The Sacred Alchemy Heart
export const LoveAlchemyIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 6px rgba(245, 158, 11, 0.4))' }}
  >
    {/* Outer alchemical circle */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <circle cx="24" cy="24" r="19" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" strokeDasharray="3 3" />
    
    {/* Heart with transformation rays */}
    <path 
      d="M24 40L8 24C8 24 4 18 8 12C12 6 18 8 24 16C30 8 36 6 40 12C44 18 40 24 40 24L24 40Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color}
      fillOpacity="0.15"
      strokeLinejoin="round"
    />
    
    {/* Inner heart glow */}
    <path 
      d="M24 34L14 24C14 24 12 20 14 16C16 12 20 13 24 19C28 13 32 12 34 16C36 20 34 24 34 24L24 34Z" 
      fill={color}
      fillOpacity="0.3"
    />
    
    {/* Transformation star in center */}
    <path 
      d="M24 18L25.5 22L30 22L26.5 25L28 30L24 27L20 30L21.5 25L18 22L22.5 22L24 18Z" 
      fill={color}
      fillOpacity="0.7"
    />
    
    {/* Radiating love beams */}
    <g stroke={color} strokeWidth="0.5" strokeOpacity="0.4">
      <line x1="24" y1="2" x2="24" y2="6" />
      <line x1="38" y1="6" x2="35" y2="9" />
      <line x1="44" y1="16" x2="40" y2="18" />
      <line x1="8" y1="16" x2="4" y2="14" />
      <line x1="10" y1="6" x2="13" y2="9" />
    </g>
  </svg>
);

// Multi-Participant / Circle of Souls Icon
export const CircleOfSoulsIcon = ({ className = "", size = 24, color = "#3B82F6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.3))' }}
  >
    {/* The great circle of belonging */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <circle cx="24" cy="24" r="16" stroke={color} strokeWidth="1" strokeOpacity="0.4" fill={color} fillOpacity="0.03" />
    
    {/* Three souls in sacred triangle formation */}
    {/* Top soul */}
    <circle cx="24" cy="10" r="5" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <circle cx="24" cy="10" r="2" fill={color} fillOpacity="0.5" />
    
    {/* Bottom left soul */}
    <circle cx="12" cy="32" r="5" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <circle cx="12" cy="32" r="2" fill={color} fillOpacity="0.5" />
    
    {/* Bottom right soul */}
    <circle cx="36" cy="32" r="5" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <circle cx="36" cy="32" r="2" fill={color} fillOpacity="0.5" />
    
    {/* Connection lines - the bonds */}
    <path d="M24 15L12 27M24 15L36 27M12 32L36 32" stroke={color} strokeWidth="1" strokeOpacity="0.3" fill="none" />
    
    {/* Center unity point */}
    <circle cx="24" cy="24" r="4" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.25" />
    <circle cx="24" cy="24" r="2" fill={color} fillOpacity="0.6" />
  </svg>
);

// Safe Space / Sacred Sanctuary Icon  
export const SanctuaryIcon = ({ className = "", size = 24, color = "#10B981" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.3))' }}
  >
    {/* Protective outer boundary */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    
    {/* Temple/sanctuary shape */}
    <path 
      d="M24 4L40 16V40H8V16L24 4Z" 
      stroke={color} 
      strokeWidth="1" 
      strokeOpacity="0.6"
      fill={color}
      fillOpacity="0.08"
      strokeLinejoin="round"
    />
    
    {/* Protective dome */}
    <path 
      d="M8 16Q24 2 40 16" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeOpacity="0.8"
      fill="none"
    />
    
    {/* Inner sanctum */}
    <path 
      d="M24 14L34 22V38H14V22L24 14Z" 
      stroke={color} 
      strokeWidth="0.5" 
      strokeOpacity="0.4"
      fill={color}
      fillOpacity="0.1"
    />
    
    {/* Sacred flame of safety */}
    <path 
      d="M24 26C24 26 20 30 24 36C28 30 24 26 24 26Z" 
      fill={color} 
      fillOpacity="0.5"
    />
    <path 
      d="M24 28C24 28 22 31 24 34C26 31 24 28 24 28Z" 
      fill={color} 
      fillOpacity="0.8"
    />
    
    {/* Doorway - the threshold of trust */}
    <rect x="20" y="32" width="8" height="8" stroke={color} strokeWidth="0.5" fill={color} fillOpacity="0.15" />
  </svg>
);

// Save Progress / Eternal Memory Icon
export const EternalMemoryIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.3))' }}
  >
    {/* Infinity/eternity circle */}
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    
    {/* Scroll/book of records */}
    <path 
      d="M10 8C10 6 12 4 14 4H34C36 4 38 6 38 8V40C38 42 36 44 34 44H14C12 44 10 42 10 40V8Z" 
      stroke={color} 
      strokeWidth="1" 
      fill={color}
      fillOpacity="0.1"
    />
    
    {/* Page lines - recorded wisdom */}
    <g stroke={color} strokeWidth="0.5" strokeOpacity="0.3">
      <line x1="16" y1="14" x2="32" y2="14" />
      <line x1="16" y1="20" x2="32" y2="20" />
      <line x1="16" y1="26" x2="28" y2="26" />
      <line x1="16" y1="32" x2="30" y2="32" />
    </g>
    
    {/* Bookmark ribbon - saved moment */}
    <path 
      d="M30 4V18L33 15L36 18V4" 
      fill={color}
      fillOpacity="0.4"
    />
    
    {/* Time spiral in corner */}
    <circle cx="20" cy="38" r="4" stroke={color} strokeWidth="0.8" fill={color} fillOpacity="0.2" />
    <path 
      d="M20 36L20 38L22 38" 
      stroke={color} 
      strokeWidth="1" 
      strokeOpacity="0.6"
      strokeLinecap="round"
    />
    
    {/* Checkmark - preserved */}
    <path 
      d="M28 36L30 38L34 34" 
      stroke={color} 
      strokeWidth="1.5" 
      strokeOpacity="0.7"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Plus Icon - Creation/Addition
export const PlusIcon = ({ className = "", size = 24, color = "#10B981" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 3px rgba(16, 185, 129, 0.3))' }}
  >
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="1" strokeOpacity="0.4" fill={color} fillOpacity="0.05" />
    <path d="M24 12V36M12 24H36" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeOpacity="0.8" />
    <circle cx="24" cy="24" r="4" fill={color} fillOpacity="0.2" />
  </svg>
);

// Trash Icon - Release/Transform
export const TrashIcon = ({ className = "", size = 24, color = "#EF4444" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 3px rgba(239, 68, 68, 0.3))' }}
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path d="M14 16H34M18 16V12H30V16M20 20V36M24 20V36M28 20V36M16 16V40C16 41 17 42 18 42H30C31 42 32 41 32 40V16" 
      stroke={color} strokeWidth="1.5" strokeOpacity="0.7" fill={color} fillOpacity="0.05" strokeLinejoin="round" />
  </svg>
);

// Copy Icon - Duplication
export const CopyIcon = ({ className = "", size = 24, color = "#6366F1" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 3px rgba(99, 102, 241, 0.3))' }}
  >
    <rect x="14" y="14" width="24" height="28" rx="3" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.1" />
    <rect x="10" y="6" width="24" height="28" rx="3" stroke={color} strokeWidth="1" strokeOpacity="0.5" fill={color} fillOpacity="0.05" />
  </svg>
);

// Check Icon - Completion/Affirmation
export const CheckIcon = ({ className = "", size = 24, color = "#10B981" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.4))' }}
  >
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="1" strokeOpacity="0.5" fill={color} fillOpacity="0.08" />
    <path d="M14 24L22 32L36 16" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Search Icon - Discovery/Seeking
export const SearchIcon = ({ className = "", size = 24, color = "#3B82F6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 3px rgba(59, 130, 246, 0.3))' }}
  >
    <circle cx="22" cy="22" r="14" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.05" />
    <circle cx="22" cy="22" r="8" stroke={color} strokeWidth="0.8" strokeOpacity="0.4" fill="none" />
    <line x1="32" y1="32" x2="42" y2="42" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="22" cy="22" r="2" fill={color} fillOpacity="0.3" />
  </svg>
);

// Save Icon - Preservation
export const SaveIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 3px rgba(245, 158, 11, 0.3))' }}
  >
    <rect x="8" y="6" width="32" height="36" rx="3" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.08" />
    <path d="M14 6V18H34V6" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
    <rect x="26" y="10" width="4" height="6" fill={color} fillOpacity="0.4" />
    <rect x="14" y="24" width="20" height="12" rx="1" stroke={color} strokeWidth="0.8" strokeOpacity="0.4" fill={color} fillOpacity="0.1" />
  </svg>
);

// Heart Icon - Love & Connection
export const HeartIcon = ({ className = "", size = 24, color = "#F472B6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(244, 114, 182, 0.4))' }}
  >
    <path 
      d="M24 42C24 42 6 30 6 18C6 12 10.5 6 17 6C21 6 24 9 24 9C24 9 27 6 31 6C37.5 6 42 12 42 18C42 30 24 42 24 42Z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.15"
    />
    <path 
      d="M24 38C24 38 10 28 10 18C10 14 13 10 17 10C20 10 22 12 24 14C26 12 28 10 31 10C35 10 38 14 38 18C38 28 24 38 24 38Z" 
      fill={color} 
      fillOpacity="0.3"
    />
    {/* Sacred geometry inner heart */}
    <circle cx="17" cy="16" r="2" fill={color} fillOpacity="0.5" />
    <circle cx="31" cy="16" r="2" fill={color} fillOpacity="0.5" />
    <path d="M24 20L20 24L24 32L28 24L24 20Z" fill={color} fillOpacity="0.4" />
  </svg>
);

// Heart Filled Icon - Full Appreciation
export const HeartFilledIcon = ({ className = "", size = 24, color = "#10B981" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.4))' }}
  >
    <path 
      d="M24 42C24 42 6 30 6 18C6 12 10.5 6 17 6C21 6 24 9 24 9C24 9 27 6 31 6C37.5 6 42 12 42 18C42 30 24 42 24 42Z" 
      fill={color}
      stroke={color} 
      strokeWidth="1.5"
    />
    {/* Inner glow */}
    <path 
      d="M24 36C24 36 12 28 12 19C12 15 14.5 11 18 11C20.5 11 22.5 13 24 15C25.5 13 27.5 11 30 11C33.5 11 36 15 36 19C36 28 24 36 24 36Z" 
      fill="white"
      fillOpacity="0.25"
    />
  </svg>
);

// X/Close Icon - Dismissal
export const CloseIcon = ({ className = "", size = 24, color = "#9CA3AF" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 2px rgba(156, 163, 175, 0.2))' }}
  >
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="0.8" strokeOpacity="0.3" fill={color} fillOpacity="0.03" />
    <path d="M16 16L32 32M32 16L16 32" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Calendar Icon - Sacred Time
export const CalendarIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 3px rgba(139, 92, 246, 0.3))' }}
  >
    <rect x="6" y="10" width="36" height="32" rx="3" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.08" />
    <path d="M6 18H42" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
    <path d="M14 6V14M34 6V14" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <g fill={color} fillOpacity="0.4">
      <circle cx="16" cy="26" r="2" />
      <circle cx="24" cy="26" r="2" />
      <circle cx="32" cy="26" r="2" />
      <circle cx="16" cy="34" r="2" />
      <circle cx="24" cy="34" r="2" />
    </g>
  </svg>
);

// Book Icon - Knowledge/Learning
export const BookIcon = ({ className = "", size = 24, color = "#F472B6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 3px rgba(244, 114, 182, 0.3))' }}
  >
    <path d="M8 8C8 8 16 10 24 6C32 10 40 8 40 8V40C40 40 32 38 24 42C16 38 8 40 8 40V8Z" 
      stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.08" />
    <line x1="24" y1="6" x2="24" y2="42" stroke={color} strokeWidth="1" strokeOpacity="0.4" />
    <g stroke={color} strokeWidth="0.5" strokeOpacity="0.3">
      <line x1="12" y1="16" x2="20" y2="14" />
      <line x1="12" y1="24" x2="20" y2="22" />
      <line x1="28" y1="14" x2="36" y2="16" />
      <line x1="28" y1="22" x2="36" y2="24" />
    </g>
  </svg>
);

// Pen Icon - Creation/Writing
export const PenIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 3px rgba(245, 158, 11, 0.3))' }}
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <path d="M34 8L40 14L18 36L10 38L12 30L34 8Z" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.1" strokeLinejoin="round" />
    <path d="M30 12L36 18" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
    <circle cx="14" cy="34" r="2" fill={color} fillOpacity="0.4" />
  </svg>
);

// Music Icon - Harmony/Expression
export const MusicIcon = ({ className = "", size = 24, color = "#EC4899" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(236, 72, 153, 0.3))' }}
  >
    <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="0.3" strokeOpacity="0.2" fill="none" />
    <ellipse cx="14" cy="36" rx="6" ry="4" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.2" />
    <ellipse cx="34" cy="32" rx="6" ry="4" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.2" />
    <path d="M20 36V12L40 8V32" stroke={color} strokeWidth="1.5" fill="none" />
    <path d="M20 18L40 14" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
  </svg>
);

// Share Icon - Connection/Giving
export const ShareIcon = ({ className = "", size = 24, color = "#06B6D4" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 3px rgba(6, 182, 212, 0.3))' }}
  >
    <circle cx="36" cy="12" r="6" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.2" />
    <circle cx="12" cy="24" r="6" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.2" />
    <circle cx="36" cy="36" r="6" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.2" />
    <path d="M17 21L31 14M17 27L31 34" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" />
  </svg>
);

// Volume Icon - Sound/Expression
export const VolumeIcon = ({ className = "", size = 24, color = "#10B981", muted = false }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 3px rgba(16, 185, 129, 0.3))' }}
  >
    <path d="M8 18H16L26 8V40L16 30H8V18Z" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.15" />
    {!muted ? (
      <>
        <path d="M32 16C34 18 35 21 35 24C35 27 34 30 32 32" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" />
        <path d="M36 10C40 14 42 19 42 24C42 29 40 34 36 38" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" strokeOpacity="0.5" />
      </>
    ) : (
      <path d="M32 18L42 30M42 18L32 30" stroke={color} strokeWidth="2" strokeLinecap="round" />
    )}
  </svg>
);

// Chevron Icon - Navigation hint
export const ChevronIcon = ({ className = "", size = 24, color = "#9CA3AF", direction = "down" }) => {
  const rotations = { down: 0, right: -90, up: 180, left: 90 };
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 48 48" 
      fill="none" 
      className={className}
      style={{ 
        filter: 'drop-shadow(0 0 2px rgba(156, 163, 175, 0.2))',
        transform: `rotate(${rotations[direction] || 0}deg)`
      }}
    >
      <path d="M12 18L24 30L36 18" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// UserPlus Icon - Invitation/Connection
export const UserPlusIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 3px rgba(139, 92, 246, 0.3))' }}
  >
    <circle cx="20" cy="16" r="8" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.15" />
    <path d="M6 44C6 34 12 28 20 28C28 28 34 34 34 44" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.1" />
    <path d="M36 20V32M30 26H42" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Pause Icon - Stillness/Reflection
export const PauseIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.3))' }}
  >
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="1" strokeOpacity="0.4" fill={color} fillOpacity="0.05" />
    <rect x="16" y="14" width="5" height="20" rx="1" fill={color} fillOpacity="0.6" />
    <rect x="27" y="14" width="5" height="20" rx="1" fill={color} fillOpacity="0.6" />
  </svg>
);

// FireIcon - Urgent/Priority (flame representing urgency)
export const FireIcon = ({ className = "", size = 24, color = "#EF4444" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 6px rgba(239, 68, 68, 0.4))' }}
  >
    {/* Base flame */}
    <path 
      d="M24 4c0 0-12 10-12 24c0 8 5 12 12 12s12-4 12-12c0-14-12-24-12-24z" 
      fill={color} 
      fillOpacity="0.2"
      stroke={color}
      strokeWidth="1.5"
    />
    {/* Inner flame */}
    <path 
      d="M24 14c0 0-6 6-6 14c0 4 2.5 6 6 6s6-2 6-6c0-8-6-14-6-14z" 
      fill={color} 
      fillOpacity="0.5"
    />
    {/* Core flame */}
    <path 
      d="M24 22c0 0-3 4-3 8c0 2 1.5 3 3 3s3-1 3-3c0-4-3-8-3-8z" 
      fill={color}
      fillOpacity="0.8"
    />
  </svg>
);

// SunriseIcon - Today/Morning (sun rising representing the day)
export const SunriseIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 6px rgba(245, 158, 11, 0.4))' }}
  >
    {/* Horizon line */}
    <path d="M6 32h36" stroke={color} strokeWidth="2" strokeLinecap="round" />
    {/* Sun */}
    <circle cx="24" cy="28" r="8" fill={color} fillOpacity="0.3" stroke={color} strokeWidth="1.5" />
    {/* Rays */}
    <path d="M24 14v6" stroke={color} strokeWidth="2" strokeLinecap="round" />
    <path d="M12 24l4 2" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M36 24l-4 2" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M14 18l3 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M34 18l-3 3" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// WeekIcon - This Week (7-day grid pattern)
export const WeekIcon = ({ className = "", size = 24, color = "#3B82F6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.3))' }}
  >
    {/* Calendar grid */}
    <rect x="6" y="10" width="36" height="32" rx="4" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.05" />
    {/* Header bar */}
    <rect x="6" y="10" width="36" height="8" rx="4" fill={color} fillOpacity="0.2" />
    {/* 7 day dots */}
    <circle cx="12" cy="26" r="3" fill={color} fillOpacity="0.4" />
    <circle cx="20" cy="26" r="3" fill={color} fillOpacity="0.5" />
    <circle cx="28" cy="26" r="3" fill={color} fillOpacity="0.6" />
    <circle cx="36" cy="26" r="3" fill={color} fillOpacity="0.7" />
    <circle cx="12" cy="36" r="3" fill={color} fillOpacity="0.8" />
    <circle cx="20" cy="36" r="3" fill={color} fillOpacity="0.9" />
    <circle cx="28" cy="36" r="3" fill={color} />
  </svg>
);

// MatrixIcon - Eisenhower Matrix (4 quadrant grid)
export const MatrixIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.3))' }}
  >
    {/* 4 quadrants */}
    <rect x="6" y="6" width="16" height="16" rx="2" fill="#EF4444" fillOpacity="0.3" stroke="#EF4444" strokeWidth="1" />
    <rect x="26" y="6" width="16" height="16" rx="2" fill="#F59E0B" fillOpacity="0.3" stroke="#F59E0B" strokeWidth="1" />
    <rect x="6" y="26" width="16" height="16" rx="2" fill="#3B82F6" fillOpacity="0.3" stroke="#3B82F6" strokeWidth="1" />
    <rect x="26" y="26" width="16" height="16" rx="2" fill="#6B7280" fillOpacity="0.3" stroke="#6B7280" strokeWidth="1" />
    {/* Center cross lines */}
    <path d="M24 4v40M4 24h40" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
  </svg>
);

// BacklogIcon - Future/Someday (hourglass with sand)
export const BacklogIcon = ({ className = "", size = 24, color = "#6B7280" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(107, 114, 128, 0.3))' }}
  >
    {/* Hourglass frame */}
    <path 
      d="M12 6h24M12 42h24" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round"
    />
    {/* Hourglass shape */}
    <path 
      d="M14 8v10c0 2 2 4 4 5l6 3l6-3c2-1 4-3 4-5V8M14 40v-10c0-2 2-4 4-5l6-3l6 3c2 1 4 3 4 5v10" 
      stroke={color} 
      strokeWidth="1.5"
      fill={color}
      fillOpacity="0.1"
    />
    {/* Sand in bottom */}
    <path 
      d="M16 40v-6c0-1 1-2 2-3l6-2l6 2c1 1 2 2 2 3v6" 
      fill={color} 
      fillOpacity="0.4"
    />
    {/* Falling sand */}
    <circle cx="24" cy="24" r="1" fill={color} fillOpacity="0.6" />
  </svg>
);

// AllTasksIcon - Complete list (stacked cards)
export const AllTasksIcon = ({ className = "", size = 24, color = "#10B981" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.3))' }}
  >
    {/* Stacked cards */}
    <rect x="10" y="8" width="28" height="8" rx="2" fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1" />
    <rect x="10" y="18" width="28" height="8" rx="2" fill={color} fillOpacity="0.4" stroke={color} strokeWidth="1" />
    <rect x="10" y="28" width="28" height="8" rx="2" fill={color} fillOpacity="0.6" stroke={color} strokeWidth="1" />
    {/* Checkmarks */}
    <path d="M14 12l2 2l4-4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 22l2 2l4-4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 32l2 2l4-4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// RecurIcon - Recurring task (circular arrows)
export const RecurIcon = ({ className = "", size = 24, color = "#06B6D4" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(6, 182, 212, 0.3))' }}
  >
    {/* Circular path */}
    <circle cx="24" cy="24" r="14" stroke={color} strokeWidth="1" strokeOpacity="0.3" fill="none" />
    {/* Rotating arrows */}
    <path 
      d="M24 10c7.7 0 14 6.3 14 14" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round"
    />
    <path d="M38 24l-4-4M38 24l-4 4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path 
      d="M24 38c-7.7 0-14-6.3-14-14" 
      stroke={color} 
      strokeWidth="2" 
      strokeLinecap="round"
    />
    <path d="M10 24l4 4M10 24l4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// PhotoIcon - Image/Picture (stylized camera frame)
export const PhotoIcon = ({ className = "", size = 24, color = "#EC4899" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(236, 72, 153, 0.3))' }}
  >
    {/* Frame */}
    <rect x="6" y="10" width="36" height="28" rx="4" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.05" />
    {/* Mountain landscape */}
    <path d="M6 32l10-8l8 6l8-10l10 12v4c0 2-2 4-4 4H10c-2 0-4-2-4-4v-4z" fill={color} fillOpacity="0.3" />
    {/* Sun */}
    <circle cx="34" cy="18" r="4" fill={color} fillOpacity="0.5" />
  </svg>
);

// ReflectIcon - Sacred mirror for introspection/analysis
export const ReflectIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.3))' }}
  >
    {/* Outer circle - the mirror frame */}
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.05" />
    {/* Inner reflection waves */}
    <ellipse cx="24" cy="24" rx="14" ry="10" stroke={color} strokeWidth="0.8" strokeOpacity="0.4" fill="none" />
    <ellipse cx="24" cy="24" rx="10" ry="6" stroke={color} strokeWidth="0.6" strokeOpacity="0.3" fill="none" />
    {/* Center eye of awareness */}
    <circle cx="24" cy="24" r="4" fill={color} fillOpacity="0.5" />
    <circle cx="23" cy="23" r="1" fill="white" fillOpacity="0.8" />
    {/* Radiating thought lines */}
    <g stroke={color} strokeWidth="0.5" strokeOpacity="0.4">
      <line x1="24" y1="4" x2="24" y2="10" />
      <line x1="24" y1="38" x2="24" y2="44" />
      <line x1="8" y1="16" x2="14" y2="19" />
      <line x1="34" y1="29" x2="40" y2="32" />
    </g>
  </svg>
);

// InsightIcon - Lightbulb/eureka moment with sacred geometry
export const InsightIcon = ({ className = "", size = 24, color = "#FBBF24" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(251, 191, 36, 0.3))' }}
  >
    {/* Radiating light rays */}
    <g stroke={color} strokeWidth="1" strokeOpacity="0.4">
      <line x1="24" y1="2" x2="24" y2="8" />
      <line x1="8" y1="10" x2="12" y2="14" />
      <line x1="40" y1="10" x2="36" y2="14" />
      <line x1="4" y1="24" x2="10" y2="24" />
      <line x1="44" y1="24" x2="38" y2="24" />
    </g>
    {/* Bulb shape */}
    <path 
      d="M18 40h12M20 44h8M16 28c-2-3-4-6-4-10c0-7 5.5-12 12-12s12 5 12 12c0 4-2 7-4 10" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.1"
      strokeLinecap="round"
    />
    {/* Inner glow */}
    <circle cx="24" cy="18" r="5" fill={color} fillOpacity="0.4" />
    <circle cx="23" cy="17" r="1.5" fill="white" fillOpacity="0.7" />
  </svg>
);

// TranslateIcon - Sacred bridge of understanding
export const TranslateIcon = ({ className = "", size = 24, color = "#06B6D4" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(6, 182, 212, 0.3))' }}
  >
    {/* Two circles representing different languages/perspectives */}
    <circle cx="14" cy="24" r="10" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.1" />
    <circle cx="34" cy="24" r="10" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.1" />
    {/* Overlapping vesica piscis - the bridge */}
    <path 
      d="M24 14c-5.5 0-10 4.5-10 10s4.5 10 10 10c5.5 0 10-4.5 10-10s-4.5-10-10-10z" 
      fill={color} 
      fillOpacity="0.25"
    />
    {/* Arrow of transformation */}
    <path d="M18 24h12M26 20l4 4-4 4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// EnergyIcon - Zap/identify with sacred lightning
export const EnergyIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.3))' }}
  >
    {/* Outer sacred circle */}
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    {/* Lightning bolt - sacred energy */}
    <path 
      d="M26 6L14 26h10l-4 16l16-22H26l4-14z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.3"
      strokeLinejoin="round"
    />
    {/* Energy sparkles */}
    <circle cx="10" cy="18" r="1.5" fill={color} fillOpacity="0.5" />
    <circle cx="38" cy="30" r="1.5" fill={color} fillOpacity="0.5" />
  </svg>
);

// AlertIcon - Sacred warning symbol
export const AlertIcon = ({ className = "", size = 24, color = "#EF4444" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(239, 68, 68, 0.3))' }}
  >
    {/* Triangle frame */}
    <path 
      d="M24 6L4 42h40L24 6z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.1"
      strokeLinejoin="round"
    />
    {/* Exclamation */}
    <line x1="24" y1="18" x2="24" y2="30" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="24" cy="36" r="2" fill={color} />
  </svg>
);

// BellIcon - Sacred notification bell
export const BellIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.3))' }}
  >
    {/* Sound waves */}
    <path d="M8 16c-2 3-2 6 0 9" stroke={color} strokeWidth="1" strokeOpacity="0.4" strokeLinecap="round" />
    <path d="M40 16c2 3 2 6 0 9" stroke={color} strokeWidth="1" strokeOpacity="0.4" strokeLinecap="round" />
    {/* Bell body */}
    <path 
      d="M12 32c0-10 4-18 12-18s12 8 12 18H12z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.15"
    />
    {/* Bell top */}
    <circle cx="24" cy="12" r="3" fill={color} fillOpacity="0.5" />
    {/* Bell bottom */}
    <path d="M10 32h28" stroke={color} strokeWidth="2" strokeLinecap="round" />
    {/* Clapper */}
    <ellipse cx="24" cy="40" rx="4" ry="3" fill={color} fillOpacity="0.4" />
  </svg>
);

// SelfEmpathyIcon - Inward spiral of self-connection
export const SelfEmpathyIcon = ({ className = "", size = 24, color = "#F59E0B" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.3))' }}
  >
    {/* Outer aura */}
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="0.5" strokeOpacity="0.2" fill="none" />
    {/* Person silhouette */}
    <circle cx="24" cy="14" r="6" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.2" />
    <path d="M14 38c0-8 4.5-14 10-14s10 6 10 14" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.1" />
    {/* Inward spiral to heart */}
    <path 
      d="M24 24c-2 2-3 4-3 6s1 3 3 3s3-1 3-3s-1-4-3-6z" 
      fill={color} 
      fillOpacity="0.5"
    />
    {/* Inward arrows */}
    <path d="M16 20l4 4M32 20l-4 4" stroke={color} strokeWidth="1" strokeOpacity="0.5" strokeLinecap="round" />
  </svg>
);

// HonestyIcon - Voice/expression with sacred geometry  
export const HonestyIcon = ({ className = "", size = 24, color = "#3B82F6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.3))' }}
  >
    {/* Speech emanation waves */}
    <path d="M34 18c3 2 5 5 5 8s-2 6-5 8" stroke={color} strokeWidth="1" strokeOpacity="0.4" strokeLinecap="round" />
    <path d="M38 14c4 3 6 7 6 12s-2 9-6 12" stroke={color} strokeWidth="1" strokeOpacity="0.3" strokeLinecap="round" />
    {/* Face profile */}
    <path 
      d="M10 20c0-8 6-14 14-14c4 0 6 2 6 6v20c0 4-2 6-6 6c-8 0-14-6-14-14v-4z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.1"
    />
    {/* Mouth/voice */}
    <ellipse cx="22" cy="28" rx="4" ry="2" fill={color} fillOpacity="0.4" />
    {/* Eye */}
    <circle cx="20" cy="18" r="2" fill={color} fillOpacity="0.5" />
  </svg>
);

// EmpathyIcon - Outward reaching connection
export const EmpathyIcon = ({ className = "", size = 24, color = "#10B981" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.3))' }}
  >
    {/* Connection bridge */}
    <ellipse cx="24" cy="24" rx="20" ry="12" stroke={color} strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    {/* Two figures reaching toward each other */}
    <circle cx="12" cy="20" r="4" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <path d="M8 36c0-5 2-8 4-8" stroke={color} strokeWidth="1" fill="none" />
    <circle cx="36" cy="20" r="4" stroke={color} strokeWidth="1" fill={color} fillOpacity="0.2" />
    <path d="M40 36c0-5-2-8-4-8" stroke={color} strokeWidth="1" fill="none" />
    {/* Reaching hands meeting */}
    <path d="M16 26l6 2M32 26l-6 2" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    {/* Heart of connection */}
    <path 
      d="M24 32l-3-3c-1-1-1-2 0-3s2-1 3 0c1-1 2-1 3 0s1 2 0 3l-3 3z" 
      fill={color} 
      fillOpacity="0.5"
    />
  </svg>
);

// SendIcon - Sacred message arrow
export const SendIcon = ({ className = "", size = 24, color = "#06B6D4" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(6, 182, 212, 0.3))' }}
  >
    {/* Arrow body */}
    <path 
      d="M6 24l36-18l-12 18l12 18L6 24z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.15"
      strokeLinejoin="round"
    />
    {/* Inner path */}
    <path d="M6 24h24" stroke={color} strokeWidth="1" strokeOpacity="0.5" />
    {/* Energy dot */}
    <circle cx="38" cy="24" r="2" fill={color} fillOpacity="0.6" />
  </svg>
);

// ThumbsUpIcon - Approval/positive feedback
export const ThumbsUpIcon = ({ className = "", size = 24, color = "#10B981" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(16, 185, 129, 0.3))' }}
  >
    {/* Hand */}
    <path 
      d="M14 44V24h-4c-2 0-4-2-4-4v-4c0-2 2-4 4-4h8l4-8c1-2 3-2 4 0l2 8h10c2 0 4 2 4 4v16c0 2-2 4-4 4H18c-2 0-4-2-4-4z" 
      stroke={color} 
      strokeWidth="1.5" 
      fill={color} 
      fillOpacity="0.15"
      strokeLinejoin="round"
    />
    {/* Sparkle */}
    <circle cx="40" cy="10" r="2" fill={color} fillOpacity="0.5" />
    <circle cx="44" cy="16" r="1" fill={color} fillOpacity="0.3" />
  </svg>
);

// LoadingIcon - Sacred spinning circle
export const LoadingIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={`animate-spin ${className}`}
    style={{ filter: 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.3))' }}
  >
    <circle cx="24" cy="24" r="18" stroke={color} strokeWidth="2" strokeOpacity="0.2" fill="none" />
    <path 
      d="M24 6c10 0 18 8 18 18" 
      stroke={color} 
      strokeWidth="2.5" 
      strokeLinecap="round"
    />
  </svg>
);

// SettingsIcon - Sacred gear/wheel of configuration
export const SettingsIcon = ({ className = "", size = 24, color = "#94A3B8" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(148, 163, 184, 0.3))' }}
  >
    {/* Outer gear teeth */}
    <path 
      d="M24 4l2 6h-4l2-6zM24 44l-2-6h4l-2 6zM4 24l6-2v4l-6-2zM44 24l-6 2v-4l6 2z
         M9.5 9.5l5.5 3-2.8 2.8-2.7-5.8zM38.5 38.5l-5.5-3 2.8-2.8 2.7 5.8z
         M9.5 38.5l3-5.5 2.8 2.8-5.8 2.7zM38.5 9.5l-3 5.5-2.8-2.8 5.8-2.7z" 
      stroke={color} 
      strokeWidth="1" 
      fill={color} 
      fillOpacity="0.1"
    />
    {/* Inner circle */}
    <circle cx="24" cy="24" r="10" stroke={color} strokeWidth="1.5" fill={color} fillOpacity="0.05" />
    {/* Center dot */}
    <circle cx="24" cy="24" r="4" fill={color} fillOpacity="0.4" />
  </svg>
);

// QuoteIcon - Sacred quotation/wisdom marks
export const QuoteIcon = ({ className = "", size = 24, color = "#8B5CF6" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(139, 92, 246, 0.3))' }}
  >
    {/* Left quote mark */}
    <path 
      d="M8 20c0-6 4-12 12-14l1 3c-5 2-8 6-8 11v10c0 2-2 4-4 4h-4c-2 0-4-2-4-4v-6c0-2 2-4 7-4z" 
      fill={color} 
      fillOpacity="0.3"
    />
    {/* Right quote mark */}
    <path 
      d="M28 20c0-6 4-12 12-14l1 3c-5 2-8 6-8 11v10c0 2-2 4-4 4h-4c-2 0-4-2-4-4v-6c0-2 2-4 7-4z" 
      fill={color} 
      fillOpacity="0.3"
    />
    {/* Decorative sparkle */}
    <circle cx="40" cy="10" r="2" fill={color} fillOpacity="0.5" />
  </svg>
);

// M.ai.ry Icon - Wisdom Bot with stylized "M" and AI elements
export const MairyIcon = ({ className = "", size = 24, color = "#A855F7" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 48 48" 
    fill="none" 
    className={className}
    style={{ filter: 'drop-shadow(0 0 4px rgba(168, 85, 247, 0.4))' }}
  >
    {/* Outer glow circle */}
    <circle cx="24" cy="24" r="20" stroke={color} strokeWidth="1" strokeOpacity="0.3" fill={color} fillOpacity="0.05" />
    
    {/* Inner wisdom circle */}
    <circle cx="24" cy="24" r="16" stroke={color} strokeWidth="1.5" strokeOpacity="0.6" fill={color} fillOpacity="0.1" />
    
    {/* Stylized "M" - the wisdom bot's core */}
    <path 
      d="M14 32V18L20 26L24 20L28 26L34 18V32" 
      stroke={color} 
      strokeWidth="2.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
    
    {/* AI dot - left eye */}
    <circle cx="18" cy="14" r="2" fill={color} fillOpacity="0.8">
      <animate attributeName="opacity" values="0.8;0.4;0.8" dur="2s" repeatCount="indefinite" />
    </circle>
    
    {/* AI dot - right eye */}
    <circle cx="30" cy="14" r="2" fill={color} fillOpacity="0.8">
      <animate attributeName="opacity" values="0.8;0.4;0.8" dur="2s" repeatCount="indefinite" begin="0.5s" />
    </circle>
    
    {/* Wisdom sparkles */}
    <circle cx="10" cy="24" r="1.5" fill={color} fillOpacity="0.6" />
    <circle cx="38" cy="24" r="1.5" fill={color} fillOpacity="0.6" />
    <circle cx="24" cy="38" r="1.5" fill={color} fillOpacity="0.6" />
  </svg>
);

export default {
  ObserveIcon,
  FeelIcon,
  NeedIcon,
  GoldIcon,
  RequestIcon,
  CompassIcon,
  WisdomIcon,
  SparkleIcon,
  HomeIcon,
  ProfileIcon,
  ShieldIcon,
  ClockIcon,
  UsersIcon,
  BridgeIcon,
  PlayIcon,
  FlowIcon,
  ArrowIcon,
  InfoIcon,
  RefreshIcon,
  MessageIcon,
  StarIcon,
  LoveAlchemyIcon,
  CircleOfSoulsIcon,
  SanctuaryIcon,
  EternalMemoryIcon,
  PlusIcon,
  TrashIcon,
  CopyIcon,
  CheckIcon,
  SearchIcon,
  SaveIcon,
  CloseIcon,
  CalendarIcon,
  BookIcon,
  PenIcon,
  MusicIcon,
  ShareIcon,
  VolumeIcon,
  ChevronIcon,
  UserPlusIcon,
  PauseIcon,
  FireIcon,
  SunriseIcon,
  WeekIcon,
  MatrixIcon,
  BacklogIcon,
  AllTasksIcon,
  RecurIcon,
  PhotoIcon,
  ReflectIcon,
  InsightIcon,
  TranslateIcon,
  EnergyIcon,
  AlertIcon,
  BellIcon,
  SelfEmpathyIcon,
  HonestyIcon,
  EmpathyIcon,
  SendIcon,
  ThumbsUpIcon,
  LoadingIcon,
  SettingsIcon,
  QuoteIcon,
  MairyIcon,
};
